import React, { useState } from "react";
import { MessageCircle, Phone, ChevronRight, FileText, Plus } from "lucide-react";
import { useUserPhoneNumbers } from "../hooks/useUserPhoneNumbers.js";
import TestNumberBadge from "../components/TestNumberBadge.js";

const STORAGE_KEY = "smsNumberCampaigns";

// Mock campaigns: templated text for different use cases
const MOCK_CAMPAIGNS = [
  {
    id: "appointment-reminder",
    name: "Appointment reminder",
    description: "Remind patients of upcoming appointments",
    template: "Hi {{name}}, this is a reminder for your appointment on {{date}} at {{time}}. Reply YES to confirm or CANCEL to reschedule.",
    placeholders: ["name", "date", "time"],
  },
  {
    id: "follow-up",
    name: "Follow-up",
    description: "Post-call or post-visit follow-up",
    template: "Hi {{name}}, thank you for speaking with us. If you have any questions, reply to this message or call us at {{phone}}.",
    placeholders: ["name", "phone"],
  },
  {
    id: "survey",
    name: "Survey link",
    description: "Send a short survey link",
    template: "Hi {{name}}, we'd love your feedback! Please take 1 minute to complete our survey: {{link}}",
    placeholders: ["name", "link"],
  },
  {
    id: "promotion",
    name: "Promotion / offer",
    description: "Promotional or seasonal offer",
    template: "Hi {{name}}, {{offer}}. Use code {{code}} by {{expiry}}. Reply STOP to opt out.",
    placeholders: ["name", "offer", "code", "expiry"],
  },
  {
    id: "general",
    name: "General message",
    description: "Custom one-off message",
    template: "Hi {{name}}, {{message}}",
    placeholders: ["name", "message"],
  },
];

function loadConfig() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error(e);
  }
  return {};
}

function saveConfig(config) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
}

export default function SMS() {
  const { allNumbers, loading: loadingNumbers } = useUserPhoneNumbers();
  const [config, setConfig] = useState(loadConfig);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");

  const selectedCampaign = MOCK_CAMPAIGNS.find((c) => c.id === selectedCampaignId);
  const connectedCampaignId = selectedNumber ? config[selectedNumber] : null;

  const handleConnectCampaign = () => {
    if (!selectedNumber || !selectedCampaignId) return;
    const next = { ...config, [selectedNumber]: selectedCampaignId };
    setConfig(next);
    saveConfig(next);
  };

  const handleDisconnectCampaign = () => {
    if (!selectedNumber) return;
    const next = { ...config };
    delete next[selectedNumber];
    setConfig(next);
    saveConfig(next);
    setSelectedCampaignId("");
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-white">
      {/* Header */}
      <header className="shrink-0 px-4 md:px-6 py-3 md:py-4 bg-gray-100 rounded-b-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#459EA3] to-[#27AAE1] flex items-center justify-center text-white shadow-md shadow-[#459EA3]/20">
            <MessageCircle className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight">SMS</h1>
            <p className="text-xs text-gray-500 hidden sm:block">
              Choose a phone number and connect it to a campaign
            </p>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden flex-nowrap">
        {/* Phone number list */}
        <aside className="w-64 md:w-72 shrink-0 flex flex-col min-h-0 bg-gray-100 rounded-r-2xl overflow-hidden">
          <div className="pt-4 pb-3 px-3 border-b border-gray-200 shrink-0">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Your numbers
            </p>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-2 space-y-2">
            {loadingNumbers ? (
              <p className="text-xs text-gray-500 px-2">Loading numbers…</p>
            ) : (
              allNumbers.map((item) => {
                const num = item.number;
                const isSelected = selectedNumber === num;
                const campaignId = config[num];
                const campaign = MOCK_CAMPAIGNS.find((c) => c.id === campaignId);
                return (
                  <button
                    key={num}
                    type="button"
                    onClick={() => {
                      setSelectedNumber(num);
                      setSelectedCampaignId(campaignId || "");
                    }}
                    className={`w-full text-left rounded-2xl p-4 transition-all duration-200 ${
                      isSelected
                        ? "bg-gradient-to-br from-[#459EA3] to-[#3d8a8e] text-white shadow-lg shadow-[#459EA3]/25"
                        : "bg-white border border-gray-200/80 hover:border-[#459EA3]/40 hover:shadow-md text-gray-800"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isSelected ? "bg-white/20" : "bg-gray-100"
                        }`}
                      >
                        <Phone className={`w-5 h-5 ${isSelected ? "text-white" : "text-gray-500"}`} />
                      </div>
                      <span className="font-semibold text-sm truncate">{item.display}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      {item.isTest && (
                        <TestNumberBadge className={isSelected ? "[&_span]:bg-white/25 [&_span]:text-white [&_span]:border-white/30" : ""} />
                      )}
                      {campaign && (
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-md ${
                            isSelected ? "bg-white/25 text-white" : "bg-[#459EA3]/10 text-[#3d8a8e]"
                          }`}
                        >
                          {campaign.name}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Detail: connect to campaign - aligned top/bottom with aside, no grey border */}
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden bg-gray-100 rounded-l-2xl rounded-b-2xl">
          {!selectedNumber ? (
            <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center px-6">
              <div className="w-16 h-16 rounded-2xl bg-[#DBF4F3]/60 flex items-center justify-center mb-3">
                <MessageCircle className="w-8 h-8 text-[#459EA3]" />
              </div>
              <p className="text-gray-600 font-medium text-sm">Select a phone number</p>
              <p className="text-xs text-gray-500 mt-1 max-w-xs">
                Choose a number to connect it to an SMS campaign. Campaigns use templated text for reminders, follow-ups, surveys, and more.
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto w-full">
              <div className="pt-4 pb-3 px-4 md:px-6 border-b border-gray-200 w-full min-w-0 shrink-0">
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-base font-bold text-gray-900">
                    {allNumbers.find((n) => n.number === selectedNumber)?.display ?? selectedNumber}
                  </h2>
                  {selectedNumber && allNumbers.find((n) => n.number === selectedNumber)?.isTest && (
                    <TestNumberBadge />
                  )}
                </div>
                <p className="text-sm text-gray-500 mb-0">
                  Connect this number to a campaign to send templated SMS messages.
                </p>
              </div>
              <div className="px-4 md:px-6 py-4 pb-6 w-full min-w-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
                  {/* Left: Current connection + Campaign selector */}
                  <div className="flex flex-col gap-6 min-w-0">
                    {/* Current connection */}
                    {connectedCampaignId && (
                      <div className="p-4 rounded-2xl bg-[#DBF4F3] border border-[#459EA3]/20 w-full">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Connected campaign
                        </p>
                        <p className="font-semibold text-gray-900">
                          {MOCK_CAMPAIGNS.find((c) => c.id === connectedCampaignId)?.name ?? connectedCampaignId}
                        </p>
                        <button
                          type="button"
                          onClick={handleDisconnectCampaign}
                          className="mt-2 text-sm text-red-600 hover:underline"
                        >
                          Disconnect campaign
                        </button>
                      </div>
                    )}

                    {/* Campaign selector */}
                    <div className="rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden w-full flex-1 flex flex-col min-h-0">
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 shrink-0">
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#459EA3]" />
                          Connect to campaign
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Campaigns are templated messages for different use cases.
                        </p>
                      </div>
                      <div className="p-4 space-y-4 flex-1 min-h-0">
                        <label className="block w-full">
                          <span className="block text-xs font-semibold text-gray-600 mb-2">
                            Choose a campaign
                          </span>
                          <select
                            value={selectedCampaignId}
                            onChange={(e) => setSelectedCampaignId(e.target.value)}
                            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:ring-2 focus:ring-[#459EA3] focus:border-[#459EA3]"
                          >
                            <option value="">— Select campaign —</option>
                            {MOCK_CAMPAIGNS.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name} – {c.description}
                              </option>
                            ))}
                          </select>
                        </label>
                        {selectedCampaign && (
                          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 w-full min-h-0">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Template preview
                            </p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap font-mono break-words">
                              {selectedCampaign.template}
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                              Placeholders: {selectedCampaign.placeholders.map((p) => `{{${p}}}`).join(", ")}
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleConnectCampaign}
                          disabled={!selectedCampaignId}
                          className="w-full rounded-xl bg-[#459EA3] text-white py-3 px-4 font-semibold text-sm hover:bg-[#3d8a8e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {connectedCampaignId === selectedCampaignId ? "Already connected" : "Connect campaign"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Right: Available campaigns - full width on small, grid on large */}
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Plus className="w-4 h-4 text-[#459EA3]" />
                      Available campaigns
                    </h3>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 w-full">
                      {MOCK_CAMPAIGNS.map((c) => (
                        <div
                          key={c.id}
                          className="rounded-2xl bg-white border border-gray-200/80 p-4 shadow-sm hover:border-[#459EA3]/30 transition-colors w-full min-w-0"
                        >
                          <div className="flex items-start justify-between gap-3 min-w-0">
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-900">{c.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>
                              <p className="text-xs text-gray-600 mt-2 font-mono bg-gray-50 rounded-lg p-2 break-words">
                                {c.template}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
